import { IBadgeList } from '@faire/web-api--source/indigofair/badges/IBadgeList';
import { IBrandSearchQueryUXMessaging } from '@faire/web-api--source/indigofair/data/IBrandSearchQueryUXMessaging';
import { IFilterOptionWithImage } from '@faire/web-api--source/indigofair/data/IFilterOptionWithImage';
import { IFilterSection } from '@faire/web-api--source/indigofair/data/IFilterSection';
import { ILeanBrandTile } from '@faire/web-api--source/indigofair/data/ILeanBrandTile';
import { ILeanProductTile } from '@faire/web-api--source/indigofair/data/ILeanProductTile';
import { IMoreInC3 } from '@faire/web-api--source/indigofair/data/IMoreInC3';
import { IPaginationData } from '@faire/web-api--source/indigofair/data/IPaginationData';
import { IPostRankingProduct } from '@faire/web-api--source/indigofair/data/IPostRankingProduct';
import { IQuerySpellCheckResponse } from '@faire/web-api--source/indigofair/data/IQuerySpellCheckResponse';
import { IRankingCandidateDebugInfo } from '@faire/web-api--source/indigofair/data/IRankingCandidateDebugInfo';
import { IRetrievalAndRankingDebugInfo } from '@faire/web-api--source/indigofair/data/IRetrievalAndRankingDebugInfo';
import { IRetrievalQuery } from '@faire/web-api--source/indigofair/data/IRetrievalQuery';
import { ISearchRecommendation } from '@faire/web-api--source/indigofair/data/ISearchRecommendation';
import { ISuggestedC3Category } from '@faire/web-api--source/indigofair/data/ISuggestedC3Category';
import { ISuggestedCategory } from '@faire/web-api--source/indigofair/data/ISuggestedCategory';

export namespace IQueryProductsV2WithLeanTileResponse {
  export enum OrganicRelevanceClassification {
    ORGANIC_RELEVANCE_CLASSIFICATION_UNKNOWN = 'ORGANIC_RELEVANCE_CLASSIFICATION_UNKNOWN',
    LOW = 'LOW',
    MEDIUM = 'MEDIUM',
    HIGH = 'HIGH',
  }

  export namespace IBrandWithProductTiles {
    /**
     * Return an instance of IBrandWithProductTiles with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (
      partial?: Partial<IBrandWithProductTiles>
    ): IBrandWithProductTiles =>
      ({
        product_tiles: [],
        ...(partial ?? {}),
      }) as IBrandWithProductTiles;
  }

  export interface IBrandWithProductTiles {
    brand_tile: ILeanProductTile.IBrand | undefined;
    product_tiles: Array<ILeanProductTile>;
  }

  export namespace IBrandOffFaireQueryResponse {
    /**
     * Return an instance of IBrandOffFaireQueryResponse with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (
      partial?: Partial<IBrandOffFaireQueryResponse>
    ): IBrandOffFaireQueryResponse =>
      ({
        brand_off_faire_related_brands_info: [],
        ...(partial ?? {}),
      }) as IBrandOffFaireQueryResponse;
  }

  export interface IBrandOffFaireQueryResponse {
    brand_off_faire_related_brands_info: Array<IQueryProductsV2WithLeanTileResponse.IBrandWithProductTiles>;
    brand_off_faire_brand_name: string | undefined;
  }

  /**
   * Redacted copy of IQueryProductsV2WithLeanTileResponse (i.e. without redacted fields).
   */
  export type Redacted = Omit<
    IQueryProductsV2WithLeanTileResponse,
    | 'badges'
    | 'suggested_brand_cta_text'
    | 'suggested_brand_token'
    | 'brand_related_products'
    | 'irrelevant_products'
    | 'ranking_debug_info_for_products'
    | 'retrieval_and_ranking_debug_info'
    | 'products'
    | 'fallback_products'
    | 'brand_tokens'
  >;

  /**
   * Return an instance of IQueryProductsV2WithLeanTileResponse with the provided fields. Any field not
   * listed will be populated with an empty value (an empty array,
   * or undefined, whichever is valid for the type of the value.)
   */
  export const build = (
    partial?: Partial<IQueryProductsV2WithLeanTileResponse>
  ): IQueryProductsV2WithLeanTileResponse =>
    ({
      product_tiles: [],
      brands_by_token: {},
      brand_tiles_related_to_search: [],
      filter_sections: [],
      related_search_terms: [],
      related_search_terms_pills: [],
      empty_suggestion_brands: [],
      visual_filter_options: [],
      badges: {},
      brand_related_product_tiles: [],
      fallback_product_tiles: [],
      suggested_categories_broad_query: [],
      retrieval_queries: [],
      brand_related_products: [],
      irrelevant_products: [],
      irrelevant_product_tiles: [],
      ranking_debug_info_for_products: [],
      products: [],
      fallback_products: [],
      brand_tokens: [],
      suggested_c3_categories: [],
      ...(partial ?? {}),
    }) as IQueryProductsV2WithLeanTileResponse;
}

export interface IQueryProductsV2WithLeanTileResponse {
  product_tiles: Array<ILeanProductTile>;
  brands_by_token: Partial<Record<string, ILeanProductTile.IBrand>>;
  /**
   * @deprecated
   */
  brand_tiles_related_to_search: Array<ILeanBrandTile>;
  pagination_data: IPaginationData | undefined;
  /**
   * This field can be used by the frontends to generate the filters.
   * Every filter is grouped into a section and has a list of possible options, of which one (or multiple) can be
   * selected as a filtering.
   * Each FilterOption has a unique identifying key that can be added to the next request in the `filterKeys`
   * list.
   * This field is only returned when request via the `returnFilterSections` option on the request.
   */
  filter_sections: Array<IFilterSection>;
  /**
   * Unique ID for the request. Must be supplied to /click-track endpoint
   * whenever a result from this response is clicked by the user.
   */
  request_id: string | undefined;
  related_search_terms: Array<string>;
  related_search_terms_pills: Array<ISearchRecommendation>;
  /**
   * When a query is about a brand name that we don't carry, sometimes we instead return products from brands
   * that our machine learning pipeline discovered as similar brands.
   * @deprecated
   */
  returning_related_brand_results: boolean | undefined;
  /**
   * When a query returns no results but contains more than 1 word, keep pruning words from the right of the
   * query and re-query Elastic Search until results are returned
   * @deprecated
   */
  returning_pruned_query_results: boolean | undefined;
  /**
   * When results are returned from a pruned query, save the pruned query
   */
  pruned_query: string | undefined;
  /**
   * TODO(FD-251000): clean this up (never populated)
   * @deprecated
   */
  empty_suggestion_brands: Array<ILeanBrandTile>;
  /**
   * When a query has less than 3 search results, embed the query via USE and look up products in Elastic search
   * @deprecated
   */
  returning_semantically_similar_results: boolean | undefined;
  /**
   * List of filter options that should display with images
   * @deprecated
   */
  visual_filter_options: Array<IFilterOptionWithImage>;
  category_name: string | undefined;
  category_url_id: string | undefined;
  /**
   * @protobufOption (indigofair.data.redacted) = true
   */
  badges: Partial<Record<string, IBadgeList>>; // redacted
  suggested_brand_info: ILeanProductTile.IBrand | undefined;
  /**
   * @protobufOption (indigofair.data.redacted) = true
   */
  suggested_brand_cta_text: string | undefined; // redacted
  brand_related_product_tiles: Array<ILeanProductTile>;
  insider_membership_message: IBrandSearchQueryUXMessaging | undefined;
  /**
   * @deprecated
   */
  fallback_product_tiles: Array<ILeanProductTile>;
  total_primary_results: number | undefined;
  suggested_categories_broad_query: Array<ISuggestedCategory>;
  /**
   * FE can request for this using returnRelatedExploreKeyword, and it will be populated with an explore page term
   * related to the search query if an explore page exists for it.
   */
  related_explore_keyword: string | undefined;
  /**
   * The issued Retrieval Queries for debugging
   */
  retrieval_queries: Array<IRetrievalQuery>;
  /**
   * @protobufOption (.faire.common.tokenType) = BRAND
   * @protobufOption (indigofair.data.redacted) = true
   */
  suggested_brand_token: string | undefined; // redacted
  /**
   * @protobufOption (indigofair.data.redacted) = true
   */
  brand_related_products: Array<IPostRankingProduct>; // redacted
  /**
   * @protobufOption (indigofair.data.redacted) = true
   */
  irrelevant_products: Array<IPostRankingProduct>; // redacted
  organic_relevance_classification:
    | keyof typeof IQueryProductsV2WithLeanTileResponse.OrganicRelevanceClassification
    | undefined;
  irrelevant_product_tiles: Array<ILeanProductTile>;
  /**
   * @deprecated
   * @protobufOption (indigofair.data.redacted) = true
   */
  ranking_debug_info_for_products: Array<IRankingCandidateDebugInfo>; // redacted
  /**
   * @protobufOption (indigofair.data.redacted) = true
   */
  retrieval_and_ranking_debug_info: IRetrievalAndRankingDebugInfo | undefined; // redacted
  /**
   * Temporary product data used only while forming the response, to support migration towards optimized data fetch for
   * lean proto endpoints.
   * @protobufOption (indigofair.data.redacted) = true
   */
  products: Array<IPostRankingProduct>; // redacted
  /**
   * @deprecated
   * @protobufOption (indigofair.data.redacted) = true
   */
  fallback_products: Array<IPostRankingProduct>; // redacted
  /**
   * @protobufOption (indigofair.data.redacted) = true
   */
  brand_tokens: Array<string>; // redacted
  /**
   * Toggle to switch between brands off Faire results view and the regular product results view
   */
  show_brand_off_faire_toggle: boolean | undefined;
  /**
   * This field is for brands off Faire query response. It is an alternative query response to the regular
   * query product response. Since it is an alternative response, whenever this field is populated, fields
   * of the regular query product response will not be populated.
   */
  brand_off_faire_query_response:
    | IQueryProductsV2WithLeanTileResponse.IBrandOffFaireQueryResponse
    | undefined;
  formatted_total_primary_results_count: string | undefined;
  query_spell_check_response: IQuerySpellCheckResponse | undefined;
  suggested_c3_categories: Array<ISuggestedC3Category>;
  /**
   * @deprecated
   */
  show_auto_filtered_results_banner: boolean | undefined;
  /**
   * display for related terms in the same C3 *
   */
  more_in_c3: IMoreInC3 | undefined;
}
